import { FrontendEvaluation } from "@client/components/round/allocator/useEvaluatorEvaluationsCache";
import ProposalLink from "@client/components/utils/ProposalLink";
import { Tooltip } from "@client/components/utils/Tooltip";
import PencilSquareIcon from "@client/icons/PencilSquareIcon";
import { StateContext, useEvaluableControls } from "@client/pages/round/Round";
import { NodeColors } from "@client/utils/NodeColors";
import { RecentIndicator } from "@client/utils/recentProposals";
import { useTheme } from "@client/utils/useTheme";
import { TinyColor } from "@ctrl/tinycolor";
import { isHold } from "@engine/round/accessors";
import { Evaluation, Funder, Recommender } from "@engine/round/aggregate";
import { Evaluable } from "@lib/types";
import { useHover } from "@lib/utils/useHover";
import { useContext } from "react";
import { EvaluableCardControls } from "./EvaluableCardControls";

export function EvaluableCard({
  evaluator,
  evaluable,
  evaluation,
  children,
  style,
}: {
  evaluator?: Recommender | Funder;
  evaluable: Evaluable;
  evaluation?: FrontendEvaluation | Evaluation;
  children?: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const { round, actingAsUserId } = useContext(StateContext);
  const evaluableControls = useEvaluableControls(evaluable.id);
  const { isDarkMode } = useTheme();

  const hasNote =
    !!round.members[actingAsUserId]?.evaluableNotes[evaluable.id]?.trim();

  const isEvaluated =
    evaluator &&
    evaluator.evaluations[evaluable.id] &&
    evaluator.evaluations[evaluable.id].state === "evaluated";

  const highlightColor = NodeColors.getColor(round, evaluable);
  const backgroundColor = new TinyColor(highlightColor)
    .setAlpha(isDarkMode ? 0.5 : 0.7)
    .toHslString();

  const card = (
    <div
      className="relative flex items-center pl-2.5 pr-1 whitespace-nowrap group text-sm border-l-12 no-underline cursor-pointer"
      onMouseEnter={() => evaluableControls.hover()}
      onMouseLeave={() => evaluableControls.unhover()}
      style={{
        height: 30,
        borderLeftColor: isEvaluated ? highlightColor : backgroundColor,
        backgroundColor: evaluableControls.isFocus
          ? highlightColor
          : backgroundColor,
        ...style,
      }}
    >
      <span className="flex-1 min-w-0 overflow-hidden text-ellipsis flex items-center">
        {evaluable.shortname}
        {hasNote && (
          <Tooltip title="You have a note for this evaluable">
            <PencilSquareIcon className="inline-block ml-1" />
          </Tooltip>
        )}
        <RecentIndicator proposalId={evaluable.id} />
      </span>
      {children}
      {!isHold(round, evaluable) && (
        <EvaluableCardControls
          evaluator={evaluator}
          evaluable={evaluable}
          evaluation={evaluation}
        />
      )}
    </div>
  );

  if (evaluable.shortname !== "Hold" && evaluable.type === "proposal") {
    return <ProposalLink proposal={evaluable}>{card}</ProposalLink>;
  }

  return card;
}

export function PlaceholderCard({
  label,
  style,
  children,
}: {
  label: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}) {
  const isEvaluated = false;
  const backgroundColor = "rgba(55, 241, 179, 0.5)";
  const highlightColor = "rgb(55, 241, 179)";
  const { hover, eventHandlers } = useHover();

  return (
    <div
      {...eventHandlers}
      className="relative flex items-center pl-2.5 pr-1 whitespace-nowrap group text-sm  border-l-12 no-underline cursor-pointer"
      style={{
        height: 30,
        borderLeftColor: isEvaluated ? highlightColor : backgroundColor,
        backgroundColor: hover ? highlightColor : backgroundColor,
        ...style,
      }}
    >
      <span className="flex-1">{label}</span>
      {children}
    </div>
  );
}
